/**
 * React Query Cache Configuration
 *
 * Defines caching strategies for different types of data based on how frequently they change.
 * All times are in milliseconds.
 *
 * NOTE: DevourPlay uses WebSocket connections for real-time updates. When data changes on the server,
 * WebSocket messages trigger cache invalidation, ensuring users always see fresh data.
 * The cache times below serve as fallback for initial loads and when WebSocket is disconnected.
 */

export const CACHE_TIMES = {
    // Static data that rarely changes
    STATIC: {
        staleTime: 30 * 24 * 60 * 60 * 1000, // 30 days
        cacheTime: 30 * 24 * 60 * 60 * 1000, // 30 days
    },

    // Semi-static data that changes occasionally
    SEMI_STATIC: {
        staleTime: 24 * 60 * 60 * 1000, // 1 day
        cacheTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    },

    // Dynamic data that changes frequently
    DYNAMIC: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
    },

    // Real-time data that needs frequent updates
    REAL_TIME: {
        staleTime: 30 * 1000, // 30 seconds
        cacheTime: 5 * 60 * 1000, // 5 minutes
    },

    // User-specific data
    USER_DATA: {
        staleTime: 2 * 60 * 1000, // 2 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
    },
} as const;

/**
 * Query retry configuration
 */
export const RETRY_CONFIG = {
    // Number of retry attempts
    retry: (failureCount: number, error: any) => {
    // Retry up to 3 times for network errors
        if (failureCount >= 3) return false;

        // Always retry network/connection errors
        if (error?.message?.includes("Network") ||
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("connection")) {
            return true;
        }

        // Don't retry 4xx client errors
        if (error?.status >= 400 && error?.status < 500) {
            return false;
        }

        return true;
    },

    // Delay between retries (exponential backoff with faster initial retry)
    retryDelay: (attemptIndex: number) => Math.min(500 * 2 ** attemptIndex, 10000),
} as const;

/**
 * Global query client configuration
 */
export const QUERY_CLIENT_CONFIG = {
    defaultOptions: {
        queries: {
            // Default stale time for all queries
            staleTime: CACHE_TIMES.DYNAMIC.staleTime,

            // Default cache time for all queries
            cacheTime: CACHE_TIMES.DYNAMIC.cacheTime,

            // Retry configuration
            ...RETRY_CONFIG,

            // Refetch on window focus
            refetchOnWindowFocus: false,

            // Refetch on reconnect
            refetchOnReconnect: "always",
        },
    },
} as const;